import { TeamStats } from '../types/curling';

/**
 * The four-row team-stats tile grid, shared between the Dashboard (full access,
 * computed client-side from every game) and the Sponsor View (condensed, fetched
 * pre-aggregated from the server) — same numbers, same layout, two different sources.
 */
export function TeamStatsTiles({ teamStats }: { teamStats: TeamStats }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="p-4 bg-blue-500/15 rounded-xl">
          <p className="text-sm text-blue-300">Total Games</p>
          <p className="text-2xl font-semibold text-white">{teamStats.gamesPlayed}</p>
        </div>
        <div className="p-4 bg-amber-500/15 rounded-xl">
          <p className="text-sm text-blue-300">Win/Loss Record</p>
          <p className="text-2xl font-semibold text-white">{teamStats.gamesWon}-{teamStats.gamesLost}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 bg-emerald-500/15 rounded-xl">
          <p className="text-sm text-blue-300">Points For</p>
          <p className="text-2xl font-semibold text-white">{teamStats.totalPoints}</p>
        </div>
        <div className="p-4 bg-red-500/15 rounded-xl">
          <p className="text-sm text-blue-300">Points Against</p>
          <p className="text-2xl font-semibold text-white">{teamStats.totalPointsAgainst}</p>
        </div>
        <div className="p-4 bg-cyan-500/15 rounded-xl">
          <p className="text-sm text-blue-300">Points Per Game</p>
          <p className="text-2xl font-semibold text-white">{teamStats.averagePointsPerGame.toFixed(1)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="p-4 bg-purple-500/15 rounded-xl">
          <p className="text-sm text-blue-300">Team Execution Score</p>
          <p className="text-2xl font-semibold text-white">
            {teamStats.teamExecutionScore !== null ? `${teamStats.teamExecutionScore.toFixed(0)}%` : '—'}
          </p>
        </div>
        <div className="p-4 bg-indigo-500/15 rounded-xl">
          <p className="text-sm text-blue-300">Traditional Score</p>
          <p className="text-2xl font-semibold text-white">{teamStats.traditionalScore.toFixed(1)}%</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 bg-orange-500/15 rounded-xl">
          <p className="text-sm text-blue-300">Heavy %</p>
          <p className="text-2xl font-semibold text-white">{teamStats.heavyPercent.toFixed(1)}%</p>
        </div>
        <div className="p-4 bg-sky-500/15 rounded-xl">
          <p className="text-sm text-blue-300">Light %</p>
          <p className="text-2xl font-semibold text-white">{teamStats.lightPercent.toFixed(1)}%</p>
        </div>
        <div className="p-4 bg-yellow-500/15 rounded-xl">
          <p className="text-sm text-blue-300">Gold Star : Bomb</p>
          <p className="text-2xl font-semibold text-white">{teamStats.goldStars} : {teamStats.bombs}</p>
        </div>
      </div>

      {/* Only absent for cached data from before these existed — both the
          client-computed and sponsor-view sources fill them in now. */}
      {teamStats.pfa !== undefined && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="p-4 bg-emerald-500/15 rounded-xl">
            <p className="text-sm text-blue-300">PFA</p>
            <p className="text-2xl font-semibold text-white">{teamStats.pfa !== null ? teamStats.pfa.toFixed(2) : '—'}</p>
            <p className="text-xs text-slate-400 mt-1">Points For / end</p>
          </div>
          <div className="p-4 bg-red-500/15 rounded-xl">
            <p className="text-sm text-blue-300">PGA</p>
            <p className="text-2xl font-semibold text-white">{teamStats.pga !== null ? teamStats.pga?.toFixed(2) : '—'}</p>
            <p className="text-xs text-slate-400 mt-1">Points Given / end</p>
          </div>
          <div className="p-4 bg-teal-500/15 rounded-xl">
            <p className="text-sm text-blue-300">SPFA</p>
            <p className="text-2xl font-semibold text-white">{teamStats.spfa !== null ? teamStats.spfa?.toFixed(2) : '—'}</p>
            <p className="text-xs text-slate-400 mt-1">Stolen Points For / end</p>
          </div>
          <div className="p-4 bg-rose-500/15 rounded-xl">
            <p className="text-sm text-blue-300">SPGA</p>
            <p className="text-2xl font-semibold text-white">{teamStats.spga !== null ? teamStats.spga?.toFixed(2) : '—'}</p>
            <p className="text-xs text-slate-400 mt-1">Stolen Points Given / end</p>
          </div>
        </div>
      )}

      {teamStats.hammerConversionRate !== undefined && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="p-4 bg-violet-500/15 rounded-xl">
            <p className="text-sm text-blue-300">Hammer Conversion</p>
            <p className="text-2xl font-semibold text-white">
              {teamStats.hammerConversionRate !== null ? `${teamStats.hammerConversionRate.toFixed(0)}%` : '—'}
            </p>
            <p className="text-xs text-slate-400 mt-1">Own hammer ends scoring 2+</p>
          </div>
          <div className="p-4 bg-fuchsia-500/15 rounded-xl">
            <p className="text-sm text-blue-300">Force %</p>
            <p className="text-2xl font-semibold text-white">
              {teamStats.forcePercent != null ? `${teamStats.forcePercent.toFixed(0)}%` : '—'}
            </p>
            <p className="text-xs text-slate-400 mt-1">Opponent hammer ends held to 1</p>
          </div>
          <div className="p-4 bg-lime-500/15 rounded-xl">
            <p className="text-sm text-blue-300">Steal Rate</p>
            <p className="text-2xl font-semibold text-white">
              {teamStats.stealRate != null ? `${teamStats.stealRate.toFixed(0)}%` : '—'}
            </p>
            <p className="text-xs text-slate-400 mt-1">Opponent hammer ends scored on</p>
          </div>
          <div className="p-4 bg-slate-500/15 rounded-xl">
            <p className="text-sm text-blue-300">Blank Rate</p>
            <p className="text-2xl font-semibold text-white">
              {teamStats.blankRate != null ? `${teamStats.blankRate.toFixed(0)}%` : '—'}
            </p>
            <p className="text-xs text-slate-400 mt-1">Ends played finishing 0-0</p>
          </div>
          <div className="p-4 bg-emerald-500/15 rounded-xl">
            <p className="text-sm text-blue-300">Big-End Rate For</p>
            <p className="text-2xl font-semibold text-white">
              {teamStats.bigEndRateFor != null ? `${teamStats.bigEndRateFor.toFixed(0)}%` : '—'}
            </p>
            <p className="text-xs text-slate-400 mt-1">Ends scoring 3+</p>
          </div>
          <div className="p-4 bg-red-500/15 rounded-xl">
            <p className="text-sm text-blue-300">Big-End Rate Against</p>
            <p className="text-2xl font-semibold text-white">
              {teamStats.bigEndRateAgainst != null ? `${teamStats.bigEndRateAgainst.toFixed(0)}%` : '—'}
            </p>
            <p className="text-xs text-slate-400 mt-1">Opponent ends scoring 3+</p>
          </div>
        </div>
      )}
    </div>
  );
}
